import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Download, Play, FileText, Bot, Activity, BookOpen, Lock, AlertTriangle, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CopyableEmail from '@/components/CopyableEmail';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  video:     { label: 'Video Lessons',     icon: Play,     color: 'amber',   accent: 'text-amber-400',   bg: 'bg-amber-400/10' },
  pdf:       { label: 'Course Materials',  icon: FileText, color: 'sky',     accent: 'text-sky-400',     bg: 'bg-sky-400/10' },
  ea:        { label: 'Expert Advisors',   icon: Bot,      color: 'violet',  accent: 'text-violet-400',  bg: 'bg-violet-400/10' },
  indicator: { label: 'Indicators',        icon: Activity, color: 'emerald', accent: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  doc:       { label: 'Documentation',     icon: BookOpen, color: 'slate',   accent: 'text-white/70',    bg: 'bg-white/10' },
  other:     { label: 'Other Files',       icon: FileText, color: 'slate',   accent: 'text-white/70',    bg: 'bg-white/10' },
};

const CATEGORY_ORDER = ['video', 'pdf', 'ea', 'indicator', 'doc', 'other'];

export default function CourseDeliveryPage() {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    axios.get(`${API}/api/my-courses/${slug}`, { withCredentials: true })
      .then((res) => setCourse(res.data))
      .catch((err) => setError(err.response?.status === 403 ? 'no-access' : 'error'))
      .finally(() => setLoading(false));
  }, [user, slug]);

  if (authLoading || loading) {
    return <div className="min-h-screen pt-24 pb-20 text-center text-white/50">Loading...</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 text-center" data-testid="course-signin">
        <Lock size={36} className="mx-auto text-amber-400 mb-4" />
        <h1 className="text-3xl font-bold text-white">Sign in to access your course</h1>
        <Link to="/login" className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-amber-400 text-black font-bold text-sm hover:bg-amber-300 transition-colors">
          Sign in
        </Link>
      </div>
    );
  }

  if (error === 'no-access') {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 text-center max-w-xl mx-auto" data-testid="course-no-access">
        <AlertTriangle size={36} className="mx-auto text-amber-400 mb-4" />
        <h1 className="text-3xl font-bold text-white">Purchase required</h1>
        <p className="mt-3 text-sm text-white/60">You don&apos;t own this course yet. If you just purchased, sign in with the email you used at checkout.</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/all-courses" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-black font-bold text-sm hover:bg-amber-300 transition-colors">
            View courses
          </Link>
          <Link to="/my-courses" className="text-sm text-white/60 hover:text-white">Back to my courses</Link>
        </div>
      </div>
    );
  }

  if (!course) {
    return <div className="min-h-screen pt-24 pb-20 text-center text-white/50">Course not found.</div>;
  }

  // Group items by category
  const grouped = (course.items || []).reduce((acc, it) => {
    const cat = CATEGORY_META[it.category] ? it.category : 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(it);
    return acc;
  }, {});

  const videoItems = grouped.video || [];

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="course-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-10 relative overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-amber-400/[0.04] blur-[140px] pointer-events-none" />
        <div className="relative mx-auto max-w-5xl">
          <Link to="/my-courses" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors mb-5" data-testid="back-link">
            <ArrowLeft size={13} /> Back to My Courses
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400/80 mb-2">Your Course</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{course.display_name}</h1>
          {course.subtitle && <p className="mt-3 text-base text-white/55">{course.subtitle}</p>}
        </div>
      </section>

      {/* Empty state — course content not uploaded yet */}
      {course.not_ready || (course.items || []).length === 0 ? (
        <section className="px-4 sm:px-6 lg:px-8" data-testid="course-not-ready">
          <div className="mx-auto max-w-3xl rounded-2xl border border-sky-400/20 bg-sky-400/[0.03] p-8 sm:p-10 text-center">
            <Clock size={32} className="mx-auto text-sky-400 mb-4" />
            <h2 className="text-xl font-bold text-white">Course materials are being prepared</h2>
            <p className="mt-3 text-sm text-white/60 leading-relaxed">
              Your purchase has been received. The course content for <span className="text-white font-medium">{course.display_name}</span> will appear on this page as soon as Hettie has finished uploading it. You&apos;ll also get an email the moment it goes live.
            </p>
            <p className="mt-2 text-xs text-white/40 flex flex-wrap items-center gap-1">
              <span>Questions? Contact</span>
              <CopyableEmail email="info@1on1trade.com" className="text-amber-400 hover:text-amber-300" iconSize={12} testId="delivery-header-email" />
            </p>
          </div>
        </section>
      ) : (
        <>
          {/* Featured video player (if any) */}
          {videoItems.length > 0 && (
            <section className="px-4 sm:px-6 lg:px-8 pb-10" data-testid="featured-video">
              <div className="mx-auto max-w-5xl">
                <div className="rounded-2xl border border-amber-400/20 bg-black overflow-hidden">
                  <video
                    src={videoItems[0].file_url}
                    controls
                    controlsList="nodownload"
                    className="w-full aspect-video bg-black"
                    data-testid="course-video-player"
                  />
                </div>
                <p className="mt-3 text-sm text-white/60">{videoItems[0].title}</p>
                {videoItems[0].description && <p className="text-xs text-white/40 mt-1">{videoItems[0].description}</p>}
              </div>
            </section>
          )}

          {/* Sections by category */}
          <section className="px-4 sm:px-6 lg:px-8 space-y-8" data-testid="course-sections">
            <div className="mx-auto max-w-5xl space-y-8">
              {CATEGORY_ORDER.map((cat) => {
                const items = grouped[cat];
                if (!items || items.length === 0) return null;
                // Skip the first video if it's already featured
                const filtered = cat === 'video' ? items.slice(1) : items;
                if (filtered.length === 0) return null;
                const meta = CATEGORY_META[cat];
                const Icon = meta.icon;
                return (
                  <div key={cat} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-7" data-testid={`section-${cat}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className={`w-10 h-10 rounded-xl ${meta.bg} flex items-center justify-center`}>
                        <Icon size={17} className={meta.accent} />
                      </div>
                      <h2 className="text-lg font-bold text-white">{meta.label}</h2>
                      <span className="text-xs text-white/30">{filtered.length} {filtered.length === 1 ? 'item' : 'items'}</span>
                    </div>
                    <div className="space-y-2">
                      {filtered.map((it, i) => (
                        <a
                          key={i}
                          href={it.file_url}
                          target="_blank"
                          rel="noreferrer"
                          download
                          onClick={() => {
                            toast.success(`Opening: ${it.title}`, {
                              description: cat === 'video' ? 'Video opening in a new tab…' : 'Download starting in a new tab…',
                              duration: 2600,
                            });
                          }}
                          className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-[#050814] border border-white/[0.06] hover:border-amber-400/30 transition-colors group"
                          data-testid={`item-${cat}-${i}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white truncate">{it.title}</p>
                            {it.description && <p className="text-xs text-white/40 truncate mt-0.5">{it.description}</p>}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {it.size && <span className="text-[11px] text-white/30 font-mono">{it.size}</span>}
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-400/10 text-amber-400 text-xs font-bold group-hover:bg-amber-400 group-hover:text-black transition-colors">
                              <Download size={12} /> Download
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Support note */}
          <section className="px-4 sm:px-6 lg:px-8 mt-10" data-testid="course-support">
            <div className="mx-auto max-w-5xl rounded-xl border border-white/[0.04] bg-white/[0.015] p-5 text-center">
              <p className="text-xs text-white/40">
              <span className="flex flex-wrap items-center gap-1">
                <span>Need help? Reach Hettie at</span>
                <CopyableEmail email="info@1on1trade.com" className="text-amber-400 hover:text-amber-300" iconSize={12} testId="delivery-footer-email" />
              </span>
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
